import { Loader } from "@react-three/drei";
import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Scene from "@/components/three/Scene";
import TopBar from "@/components/panels/TopBar";
import AttributionBar from "@/components/panels/AttributionBar";
import ControlDock from "@/components/panels/ControlDock";
import ProductPanel from "@/components/panels/ProductPanel";
import AudioBar from "@/components/panels/AudioBar";
import { useStore } from "@/store/useStore";

export default function Showroom() {
  const mobilePanel = useStore((s) => s.mobilePanel);
  const setMobilePanel = useStore((s) => s.setMobilePanel);

  // Pause auto-rotation whenever any interaction/action state changes; resume after 3s.
  useEffect(() => {
    const keys = [
      "product", "airpodSelect", "lidOpen", "leftOut", "rightOut", "anc", "spatialAudio", "eq",
      "melasylState", "routine", "skinIntensity", "fitz",
      "variant", "ringSize", "tryOn", "flashlight", "tv", "lock", "haptic",
      "explode", "shading", "opacity",
    ];
    const unsub = useStore.subscribe((state, prev) => {
      for (const k of keys) {
        if (state[k] !== prev[k]) {
          state.bump();
          break;
        }
      }
    });
    return unsub;
  }, []);

  // Detect WebXR capability once and lift into shared state.
  useEffect(() => {
    let mounted = true;
    const check = (mode) =>
      typeof navigator !== "undefined" && navigator.xr?.isSessionSupported
        ? navigator.xr.isSessionSupported(mode).catch(() => false)
        : Promise.resolve(false);
    Promise.all([check("immersive-ar"), check("immersive-vr")]).then(([ar, vr]) => {
      if (mounted) useStore.getState().setXrSupport(ar, vr);
    });
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <motion.div
      data-testid="showroom-main-container"
      className="absolute inset-0"
      initial={{ opacity: 0, scale: 1.03 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.7, ease: [0.2, 0.8, 0.2, 1] }}
    >
      {/* 3D stage — inset between the docked panels only at lg (full-bleed below) */}
      <div className="absolute inset-0 lg:left-[23rem] lg:right-[20rem]">
        <Scene />
      </div>
      <div className="vignette" />

      <TopBar />
      <AttributionBar />

      {/* Desktop docks (lg+). Below lg the panels open as drawers. */}
      <div className="hidden lg:block">
        <ProductPanel />
        <ControlDock />
      </div>

      {/* Mobile drawers */}
      <AnimatePresence>
        {mobilePanel && (
          <motion.div
            className="fixed inset-0 z-[55] lg:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div
              className="absolute inset-0 bg-black/55"
              onClick={() => setMobilePanel(null)}
              data-testid="mobile-panel-backdrop"
            />
            <motion.div
              className="absolute bottom-0 left-0 right-0 max-h-[78vh] overflow-y-auto scroll-thin"
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 28, stiffness: 260 }}
            >
              {mobilePanel === "product" ? <ProductPanel mobile /> : <ControlDock mobile />}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AudioBar />

      <Loader
        containerStyles={{ background: "rgba(5,7,13,0.92)" }}
        barStyles={{ background: "linear-gradient(90deg,#00f0ff,#a855f7)", height: "4px" }}
        dataStyles={{ color: "#7fe9ff", fontFamily: "Source Code Pro, monospace", fontSize: "12px" }}
        dataInterpolation={(p) => `Rendering spatial assets · ${p.toFixed(0)}%`}
      />
    </motion.div>
  );
}
